import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react';
import * as React from 'react';
import { useI18n } from '@/reactRouterPlugins';
import { FiArrowLeft as IconLeft, FiArrowRight as IconRight } from 'react-icons/fi';

import { Button } from '@/components/ui/button';
import { cn } from '@/utils/shadcn';

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: 'horizontal' | 'vertical';
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function NextCircleIcon(props: React.HTMLAttributes<HTMLOrSVGElement>) {
  const { locale } = useI18n();
  if (locale.textDirection === 'rtl') {
    return <IconLeft {...props} />;
  }
  return <IconRight {...props} />;
}

function PrevCircleIcon(props: React.HTMLAttributes<HTMLOrSVGElement>) {
  const { locale } = useI18n();
  if (locale.textDirection === 'ltr') {
    return <IconLeft {...props} />;
  }
  return <IconRight {...props} />;
}

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />');
  }

  return context;
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CarouselProps
>(({ orientation = 'horizontal', opts, setApi, plugins, className, children, ...props }, ref) => {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
    },
    plugins
  );
  const { locale } = useI18n();
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) {
      return;
    }

    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (locale.textDirection == 'rtl') {
          scrollNext();
        } else {
          scrollPrev();
        }
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (locale.textDirection == 'rtl') {
          scrollPrev();
        } else {
          scrollNext();
        }
      }
    },
    [scrollPrev, scrollNext, locale.textDirection]
  );

  React.useEffect(() => {
    if (!api || !setApi) {
      return;
    }

    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api?.off('select', onSelect);
    };
  }, [api, onSelect]);

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api: api,
        opts,
        orientation: orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext,
      }}
    >
      <div
        ref={ref}
        onKeyDownCapture={handleKeyDown}
        className={cn('g-relative', className)}
        role="region"
        aria-roledescription="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
});
Carousel.displayName = 'Carousel';

const CarouselContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { carouselRef, orientation } = useCarousel();

    return (
      <div ref={carouselRef} className="g-overflow-hidden">
        <div
          ref={ref}
          className={cn(
            'g-flex',
            orientation === 'horizontal' ? '-g-me-4' : '-g-mt-4 g-flex-col',
            className
          )}
          {...props}
        />
      </div>
    );
  }
);
CarouselContent.displayName = 'CarouselContent';

const CarouselItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { orientation } = useCarousel();

    return (
      <div
        ref={ref}
        role="g-group"
        aria-roledescription="slide"
        className={cn(
          'g-min-w-0 g-shrink-0 g-grow-0 g-basis-full',
          orientation === 'horizontal' ? 'g-pe-4' : 'g-pt-4',
          className
        )}
        {...props}
      />
    );
  }
);
CarouselItem.displayName = 'CarouselItem';

const CarouselPrevious = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
  ({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          'g-absolute  g-h-8 g-w-8 g-rounded-full',
          orientation === 'horizontal'
            ? '-g-start-12 g-top-1/2 -g-translate-y-1/2'
            : // vertical orientation: paired with physical translate-x for centering, so keep physical g-left-1/2
              '-g-top-12 g-left-1/2 -g-translate-x-1/2 g-rotate-90',
          className
        )}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
        {...props}
      >
        <PrevCircleIcon className="g-h-4 g-w-4" />
        <span className="g-sr-only">Previous slide</span>
      </Button>
    );
  }
);
CarouselPrevious.displayName = 'CarouselPrevious';

const CarouselNext = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Button>>(
  ({ className, variant = 'outline', size = 'icon', ...props }, ref) => {
    const { orientation, scrollNext, canScrollNext } = useCarousel();

    return (
      <Button
        ref={ref}
        variant={variant}
        size={size}
        className={cn(
          'g-absolute g-h-8 g-w-8 g-rounded-full',
          orientation === 'horizontal'
            ? '-g-end-12 g-top-1/2 -g-translate-y-1/2'
            : // vertical orientation: paired with physical translate-x for centering, so keep physical g-left-1/2
              '-g-bottom-12 g-left-1/2 -g-translate-x-1/2 g-rotate-90',
          className
        )}
        disabled={!canScrollNext}
        onClick={scrollNext}
        {...props}
      >
        <NextCircleIcon className="g-h-4 g-w-4" />
        <span className="g-sr-only">Next slide</span>
      </Button>
    );
  }
);
CarouselNext.displayName = 'CarouselNext';

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
};
